"use client";

import { FC, useContext, useEffect, useState } from "react";
import { Popover, PopoverProps } from "react-tiny-popover";
import { twMerge } from "tailwind-merge";
import { EditorRootContext } from "../context/editorContext";
import { ButtonProps } from "./Button";

type PopoverPannelProps = {
  size?: ButtonProps["size"];
} & PopoverProps;

let viewportBoundaryElement: HTMLDivElement | null = null;

/**
 * react-tiny-popover positions its panels with `position: fixed` and clamps them
 * within a boundary element that defaults to `document.body`. When the body is
 * sized to the viewport (e.g. `h-full`) while the page is scrolled, the body's
 * bounding rect sits outside the viewport, which pushes nudged/repositioned
 * popovers off-screen. The correct boundary for a fixed-positioned panel is the
 * visible viewport, so we share a single hidden, full-viewport element across
 * the app and use it as the boundary.
 */
const ensureViewportBoundaryElement = (): HTMLDivElement | undefined => {
  if (typeof document === "undefined") return undefined;
  if (
    viewportBoundaryElement &&
    document.body.contains(viewportBoundaryElement)
  ) {
    return viewportBoundaryElement;
  }
  const el = document.createElement("div");
  el.setAttribute("data-popover-viewport-boundary", "");
  el.style.cssText =
    "position:fixed;top:0;right:0;bottom:0;left:0;pointer-events:none;visibility:hidden;z-index:-1;";
  document.body.appendChild(el);
  viewportBoundaryElement = el;
  return el;
};

const PopoverPannel: FC<PopoverPannelProps> = ({ size = "m", ...rest }) => {
  const editorRootElement = useContext(EditorRootContext);
  const parentElement = editorRootElement ?? undefined;

  const [viewportBoundary, setViewportBoundary] = useState<
    HTMLDivElement | undefined
  >(undefined);
  useEffect(() => {
    if (!parentElement) setViewportBoundary(ensureViewportBoundaryElement());
  }, [parentElement]);

  const boundaryElement =
    rest.boundaryElement ?? (parentElement ? undefined : viewportBoundary);
  const alignShift =
    rest.align == "start" ? "!-ml-1.5" : rest.align == "end" ? "ml-1.5" : "";
  const sizeClasses = {
    xs: "rounded-[8px]",
    s: "rounded-[11px]",
    m: "rounded-[14px]",
    l: "rounded-[18px]",
  };
  return (
    <Popover
      {...rest}
      padding={rest.padding ?? 4}
      boundaryInset={rest.boundaryInset ?? 4}
      parentElement={parentElement}
      boundaryElement={boundaryElement}
      containerClassName={twMerge(
        alignShift,
        sizeClasses[size],
        "popover-menu shadow-2xl flex flex-col !z-[2147483646] min-w-[160px] outline-none",
        "bg-white border border-border dark:bg-surface dark:text-foreground [&_button.tertiary:hover]:!bg-slate-100 [&_button.tertiary:focus-visible]:!bg-slate-100 [&_button.flat]:!bg-slate-150 [&_button.flat:hover]:!bg-slate-200 [&_button.flat:focus-visible]:!bg-slate-200 dark:[&_button.tertiary]:!text-foreground dark:[&_button.tertiary:hover]:!bg-surface-secondary dark:[&_button.tertiary:focus-visible]:!bg-surface-secondary dark:[&_button.flat]:!bg-surface-secondary dark:[&_button.flat:hover]:!bg-surface-tertiary dark:[&_button.flat:focus-visible]:!bg-surface-tertiary dark:[&_hr]:!bg-surface-secondary [&_button_button.tertiary:hover]:!bg-slate-200",
        rest.containerClassName,
      )}
    />
  );
};

export default PopoverPannel;
